import { useState, useEffect, useRef, useCallback } from 'react';
import { useStore, TocItem } from '../../store/useStore';
import ReaderHeader from './ReaderHeader';
import EPUBViewer from './EPUBViewer';
import PDFViewer from './PDFViewer';
import SettingsPanel from './SettingsPanel';
import TOCPanel from './TOCPanel';
import HighlightsPanel from './HighlightsPanel';
import FlipbookView, { FlipbookViewHandle } from './FlipbookView';
import SearchPanel from './SearchPanel';
import ShareModal from '../Social/ShareModal';
import BookmarksPanel from './BookmarksPanel';
import ThumbnailNav from './ThumbnailNav';
import BackgroundAudio from './BackgroundAudio';
import ePub from 'epubjs';
import * as pdfjsLib from 'pdfjs-dist';
import { getEPUBFile, getPDFFile } from '../../utils/fileStorage';
import { visiblePages } from '../../utils/spreadMap';

declare global {
  interface Window {
    // S2-3(2026-09-14): 책넘김 모드에서 "지금 펼쳐진 쪽"의 텍스트를 돌려주는 창구.
    // PDFViewer/EPUBViewer가 마운트되지 않는 모드라 Reader.tsx가 직접 등록한다.
    flipbookGetCurrentText?: () => Promise<string>;
  }
}

// PDF.js worker 설정
pdfjsLib.GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.mjs`;

type ViewMode = 'scroll' | 'flipbook';

interface FlipbookPage {
  content: string;
  pageNumber: number;
}

// SPRINT 2.2-a: EPUB 내부 상대경로("../images/x.png" 등)를 zip 안의 실제
// 경로로 정규화한다. "/"로 나눈 뒤 ".."는 직전 세그먼트를 pop, "."/빈 문자열은
// 무시 — basePath+relPath를 그냥 이어붙이면 "Text/../Images/x.png"처럼 zip에
// 없는 문자열이 나와 getBlob()이 못 찾는다(실측).
function resolveEpubPath(basePath: string, relPath: string): string {
  const parts = basePath.split('/').filter(Boolean);
  for (const seg of relPath.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') parts.pop();
    else parts.push(seg);
  }
  return parts.join('/');
}

// V2/V1: 캐시 엔트리에 페이지 배열과 함께 실제 비율(height/width)도 같이 보관한다.
// PDF만 유효한 값이 나오고(뷰포트 실측), EPUB은 고정 페이지 이미지가 없어 null —
// FlipbookView가 내부 기본값으로 대체한다.
interface FlipbookCacheEntry {
  pages: FlipbookPage[];
  pageAspectRatio: number | null;
  // S2-2 딥QA 수정(2026-09-14): 책넘김 모드에서는 PDFViewer가 마운트되지 않아
  // window.pdfSearchAllPages가 등록되지 않았다 — 검색이 항상 "결과 없음"으로만
  // 떴다(플립북 모드가 PDF 기본 모드라 사실상 검색 기능 전체가 죽어 있었음). 여기서
  // pdf.js 문서 자체를 캐시에 보관해(destroy 지연) 재사용한다.
  pdfDoc?: pdfjsLib.PDFDocumentProxy;
  pdfTextCache?: Map<number, string>;
  // 딥QA 수정(2026-09-14): TOC도 검색과 같은 이유로 책넘김 모드에서 깨져 있었다 —
  // tocItems는 PDFViewer.tsx/EPUBViewer.tsx의 마운트 이펙트에서만 채워지는데
  // 둘 다 책넘김 모드에서는 마운트 자체가 안 된다. 페이지 이미지처럼 같이 캐싱해
  // 모드를 오가도 다시 계산하지 않고 재사용한다.
  tocItems?: TocItem[];
  // PM 결정(2026-09-14): EPUB 책넘김 모드용 페이지별 평문 캐시. EPUB은 pdf.js 같은
  // 문서 객체를 들고 있을 필요가 없다 — 이미 만들어 둔 flipbook pages(HTML)에서
  // 태그만 벗기면 페이지 번호와 1:1로 맞는 검색 대상이 나온다.
  epubPageTextCache?: Map<number, string>;
  // PM 결정(2026-09-14): EPUB 목차도 EPUBViewer가 마운트될 때만 채워져서, 책넘김
  // 모드에선 패널이 "불러오는 중..."에서 멈춰 있었다(PDF와 같은 결함). 추출할 때
  // 같이 떠서 캐시해 둔다.
  epubTocItems?: TocItem[];
  // 딥QA 수정(2026-09-14): EPUB 플립북 추출은 이미지마다 blob URL을 만드는데
  // (아래 resolveImages) 이걸 한 번도 해제하지 않아, 책을 열고 닫을수록 탭에
  // blob이 쌓였다 — 이 리포에서 createObjectURL만 있고 revoke가 없는 유일한 곳이었다.
  // 페이지 HTML이 이 URL을 참조하므로 살아있는 동안은 못 지우고, Reader가 사라질 때
  // 캐시와 함께 정리한다.
  epubImageUrls?: string[];
}

const Reader = () => {
  const { currentBook, showSettings, showTOC, readerSettings, setTocItems, clearToc } = useStore();
  const [currentLocation, setCurrentLocation] = useState<string>('');
  const [showHighlights, setShowHighlights] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [showAudio, setShowAudio] = useState(false);
  // V6(현장보고 2026-08-26): 기본 모드를 책넘김으로 — 단, PDF일 때만. EPUB은 플립북
  // 조판 품질이 아직 낮아 기존대로 스크롤을 유지한다. 지연 초기화는 currentBook이
  // 이미 있는 "정상" 마운트 경로의 첫 페인트용 기본값이다.
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    currentBook?.fileType === 'pdf' ? 'flipbook' : 'scroll'
  );
  // SPRINT 1.8 QA §3: Reader.tsx 아래쪽에 `if (!currentBook) return <p>...</p>`
  // 분기가 있다는 것 자체가 "currentBook 없이 마운트되는 경로"가 실제로 존재한다는
  // 뜻이다(딥링크·자동열기 타이밍 등, currentView가 먼저 'reader'로 바뀌고
  // currentBook은 한 박자 늦게 채워지는 경우). 그 경로로 마운트되면 위 지연
  // 초기화가 'scroll'로 확정되고, 이후 currentBook이 채워져도 useState 초기화
  // 함수는 다시 안 돌아 영원히 스크롤 모드에 갇힌다. currentBook.id가 (재마운트
  // 없이) 새로 채워지거나 바뀔 때마다 fileType 기준으로 다시 맞춘다 — 단, 사용자가
  // 이 책에서 이미 수동으로 토글했으면 덮어쓰지 않는다(userToggledViewModeRef).
  const userToggledViewModeRef = useRef(false);
  const lastDefaultedBookIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!currentBook) return;
    // 이 effect는 currentBook.id 변경에만 반응하므로 지금 여기 있다는 것 자체가
    // "새 책"이라는 뜻이지만, 미래에 이 effect의 의존성 배열이 바뀌거나 다른
    // effect가 같은 책 id에 대해 다시 실행될 경우를 대비해 명시적으로 가드한다.
    if (lastDefaultedBookIdRef.current !== currentBook.id) {
      lastDefaultedBookIdRef.current = currentBook.id;
      userToggledViewModeRef.current = false; // 새 책 → 이전 책의 수동 토글 보호를 해제
    }
    if (userToggledViewModeRef.current) return; // 이 책에서 이미 수동으로 바꿨으면 덮어쓰지 않음
    setViewMode(currentBook.fileType === 'pdf' ? 'flipbook' : 'scroll');
  }, [currentBook?.id]);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    userToggledViewModeRef.current = true;
    setViewMode(mode);
  }, []);

  // PM 결정(2026-09-14): EPUB의 CFI는 책넘김 페이지 번호로 매핑할 방법이 없다(구조적 벽).
  // 그렇다고 클릭에 아무 반응이 없으면 사용자는 "고장"으로 받아들인다 — 스크롤 모드로
  // 전환해서 그 위치를 열어 준다(37e56de에서 "범위 밖"으로 미뤄둔 폴백). EPUBViewer가
  // 마운트되며 epubGoTo를 등록할 때까지 짧게 기다렸다가 호출한다.
  const openEpubLocation = useCallback((cfi?: string) => {
    if (!cfi) return false;
    // ★ 여기서 window.epubGoTo를 그냥 부르면 안 된다. 책넘김 모드에서는 아래
    //   registerFlipbookEpubGoTo가 등록한 "이 함수로 넘기는 창구"가 그 자리에 있어
    //   서로를 무한히 다시 부른다(스택 오버플로). EPUBViewer가 등록한 진짜 창구만
    //   호출해야 하므로 __flipbookBridge 표식이 붙은 것은 건너뛴다.
    const realGoTo = () => {
      const fn = window.epubGoTo as (((href: string) => void) & { __flipbookBridge?: boolean }) | undefined;
      return fn && !fn.__flipbookBridge ? fn : undefined;
    };

    const direct = realGoTo();
    if (direct) {
      direct(cfi);
      return true;
    }
    // 아직 EPUBViewer가 없다(책넘김 모드) → 스크롤 모드로 전환하고, 뷰어가 준비되면
    // 그 위치를 연다.
    //
    // ※ 한 번만 부르면 안 된다. EPUBViewer는 window.epubGoTo를 "등록"한 뒤에야 자기
    //   초기 display(표지/이어읽기)를 실행하므로, 등록되자마자 우리가 부르면 그 뒤에
    //   오는 초기 display가 우리 이동을 덮어쓴다(실측: 12장을 눌렀는데 표지로 감).
    //   그래서 준비된 뒤 몇 차례 더 호출해 마지막 호출이 초기 display보다 뒤에 오게 한다.
    //   display()를 여러 번 부르는 것 자체는 무해하다(같은 위치로 다시 이동할 뿐).
    handleViewModeChange('scroll');

    // 딥QA 재점검(2026-09-14): 이 타이머는 반드시 ref로 들고 있어야 한다. 지역 변수로만
    // 두면 (a) Reader가 언마운트돼도 살아남아, 30초 안에 다른 책을 열면 그 책에 대고
    // 이전 책의 위치로 display()를 불러버리고, (b) 목차를 연속으로 두 번 누르면 타이머가
    // 두 개 겹쳐 서로 다른 위치로 번갈아 이동한다. 새 요청이 오면 이전 타이머를 먼저 끊는다.
    if (pendingGoToTimerRef.current !== null) {
      clearInterval(pendingGoToTimerRef.current);
      pendingGoToTimerRef.current = null;
    }

    let waited = 0;
    let fired = 0;
    const stop = () => {
      if (pendingGoToTimerRef.current !== null) {
        clearInterval(pendingGoToTimerRef.current);
        pendingGoToTimerRef.current = null;
      }
    };
    pendingGoToTimerRef.current = window.setInterval(() => {
      waited += 500;
      const fn = realGoTo();
      if (fn) {
        fn(cfi);
        fired += 1;
        if (fired >= 4) {
          stop();
          return;
        }
      }
      if (waited >= 30000) {
        if (fired === 0) console.warn('EPUB 위치 이동 실패 — 뷰어가 준비되지 않음:', cfi);
        stop();
      }
    }, 500);
    return true;
  }, [handleViewModeChange]);

  const [flipbookPages, setFlipbookPages] = useState<FlipbookPage[]>([]);
  const [flipbookCurrentPage, setFlipbookCurrentPage] = useState(1);
  const [loadingFlipbook, setLoadingFlipbook] = useState(false);
  // V1: 추출된 PDF 페이지의 실제 비율(height/width, 뷰포트 실측). FlipbookView의
  // pageRatio 1.4 하드코딩을 대체한다 — EPUB이면 null(내부 기본값 사용).
  const [pageAspectRatio, setPageAspectRatio] = useState<number | null>(null);
  // V1: 썸네일 스트립(ThumbnailNav)의 실측 높이. position:fixed라 문서 흐름을 안 밀어내서
  // FlipbookView가 스스로는 알 수 없다 — 열렸을 때 실측값을 여기로 받아 내려준다.
  const [thumbnailStripHeight, setThumbnailStripHeight] = useState(0);
  // R3(SPRINT 1.9 QA): 반대 방향 — FlipbookView의 footer(하단 네비게이션) 실측 높이.
  // ThumbnailNav의 닫힘 배지가 이 값 위로 벗어나게 자기 위치를 계산한다.
  const [flipbookFooterHeight, setFlipbookFooterHeight] = useState(0);
  // S2-1(G3): 썸네일 바가 "지금 보이는 스프레드"를 강조하려면 세로/가로
  // 모드를 알아야 한다. 판정 정본은 FlipbookView 한 곳이므로 거기서 받아온다.
  const [flipbookIsPortrait, setFlipbookIsPortrait] = useState(false);
  const flipbookViewRef = useRef<FlipbookViewHandle>(null);

  // V1 → SPRINT 1.8 QA §2(회귀 수정): html/body 전역 overflow:hidden이 Library
  // 화면의 문서 스크롤까지 막아 FeatureHighlights·Footer에 영원히 도달할 수 없게
  // 만들었다(min-h-screen은 높이 하한이지 상한이 아니라 부모 자체가 스크롤 가능해야
  // 함). 원래 버그(닫힌 ThumbnailNav가 documentElement.scrollHeight에 잡히는 것)는
  // 리더 화면 안에서만 일어나므로, Reader가 마운트돼 있는 동안만 잠근다.
  // ★body에만 걸면 안 됨 — 뷰포트 스크롤바는 documentElement(html)의 overflow가
  // 결정한다(실측으로 확인: body만 hidden이어도 html이 visible이면 스크롤바가 그대로
  // 남는다). html·body 둘 다에 클래스를 건다.
  useEffect(() => {
    document.documentElement.classList.add('reader-locked');
    document.body.classList.add('reader-locked');
    return () => {
      document.documentElement.classList.remove('reader-locked');
      document.body.classList.remove('reader-locked');
    };
  }, []);

  // V2(현장보고 2026-08-26): bookId 키 캐시. 스크롤↔책넘김을 토글할 때마다 53쪽 PDF를
  // 통째로 재추출하던 문제(B6와 동일 결함, 실사용 영상에서 "페이지 추출 중" 스피너가
  // 세 번 반복)를 없앤다. Reader가 마운트돼 있는 동안(=이 책을 보는 동안)만 유지되고,
  // 책이 바뀌면(다른 bookId) 그 책은 캐시에 없으니 자연히 새로 추출된다. 라이브러리로
  // 나가면 Reader 자체가 unmount되어 캐시도 함께 사라진다 — 그걸로 충분하다(같은 책을
  // 다시 열었을 때 한 번 더 추출되는 정도는 "토글마다 매번"에 비하면 사소하다).
  // openEpubLocation이 띄우는 재시도 타이머(위 주석 참고). 언마운트 때 반드시 끊는다.
  const pendingGoToTimerRef = useRef<number | null>(null);
  const flipbookCacheRef = useRef<Map<string, FlipbookCacheEntry>>(new Map());
  // SPRINT 1.9 QA §1(긴급): 추출 effect가 같은 bookId로 동시에 두 번 실행되면
  // pdf.js가 같은 ArrayBuffer를 워커로 두 번 transfer하려다 양쪽 다 영구 pending된다
  // (getDocument().promise가 끝나지 않음 — 실사용에서 100% 재현, 책이 아예 안 열림).
  // 정확한 트리거는 로그로 확정하지 못했지만(§ 아래 커밋 메시지 참고), 원인과 무관하게
  // bookId별 "진행 중" Promise를 여기 보관해 이미 진행 중이면 새로 시작하지 않는다 —
  // effect가 몇 번 재실행되든 실제 추출은 bookId당 최대 1개만 살아있게 만드는 구조적 가드.
  const inFlightExtractionRef = useRef<Map<string, Promise<void>>>(new Map());
  // 추출 도중 화면을 떠났을 때 "이 책의 추출 결과는 버린다"는 표식(아래 effect 참고).
  const extractionCancelledRef = useRef<Set<string>>(new Set());

  // PM 결정(2026-09-14): 책넘김 모드의 EPUB 목차 이동. TOCPanel.tsx는 그대로
  // window.epubGoTo(item.href)를 부르는데 그 창구는 EPUBViewer.tsx에만 있다.
  // EPUB의 href는 flipbook 페이지 번호로 매핑할 수 없으므로(구조적 벽), 같은 이름의
  // 창구를 열어 두고 openEpubLocation으로 넘긴다 — 스크롤 모드로 전환한 뒤 그 위치를
  // 연다. 무반응보다는 "모드가 바뀌며 해당 위치로 간다"가 낫다는 판단.
  const registerFlipbookEpubGoTo = () => {
    const bridge = (href: string) => {
      openEpubLocation(href);
    };
    // openEpubLocation이 이 창구를 "진짜 EPUBViewer 창구"로 오인해 자기 자신을
    // 다시 부르지 않도록 표식을 붙인다(위 realGoTo 참고).
    (bridge as any).__flipbookBridge = true;
    window.epubGoTo = bridge;
  };

  // PM 결정(2026-09-14): 책넘김 모드의 EPUB 전체 검색. 스크롤 모드(EPUBViewer.tsx)는
  // spine을 훑어 href를 돌려주지만, 책넘김 모드는 이동 단위가 flipbook 페이지 번호라
  // 이미 만들어 둔 pages(HTML)에서 뽑은 페이지별 평문을 검색해 페이지 번호를 돌려준다.
  const registerFlipbookEpubSearch = (textCache: Map<number, string>) => {
    window.epubSearchAllPages = async (query: string) => {
      const results: { cfi: string; text: string; excerpt: string; pageNumber: number }[] = [];
      if (!query.trim()) return results;

      const MAX_RESULTS = 50;
      const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      let regex: RegExp;
      try {
        regex = new RegExp(escapeRegExp(query), 'gi');
      } catch {
        return results;
      }

      const pageNumbers = Array.from(textCache.keys()).sort((a, b) => a - b);
      for (const pageNumber of pageNumbers) {
        if (results.length >= MAX_RESULTS) break;
        const text = textCache.get(pageNumber) || '';
        if (!text) continue;

        regex.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null && results.length < MAX_RESULTS) {
          const start = Math.max(0, match.index - 30);
          const end = Math.min(text.length, match.index + query.length + 30);
          results.push({
            cfi: '',
            pageNumber,
            text: match[0],
            excerpt: `...${text.substring(start, end)}...`,
          });
          if (match.index === regex.lastIndex) regex.lastIndex++;
        }
      }

      return results;
    };
  };

  // S2-2 딥QA 수정(2026-09-14): PDFViewer.tsx에만 있던 pdfSearchAllPages를
  // 책넘김 모드에서도 쓸 수 있게 등록하는 공용 함수. 로직은 PDFViewer.tsx와
  // 동일하다 — 페이지별 텍스트를 캐시해 가며 정규식으로 훑는다.
  const registerFlipbookPdfBridge = (pdf: pdfjsLib.PDFDocumentProxy, textCache: Map<number, string>) => {
    // 딥QA 수정(2026-09-14): TOCPanel.tsx는 그대로 window.pdfGoToPage(item.pageNumber)를
    // 부른다 — PDFViewer.tsx(스크롤 모드)와 똑같은 이름의 창구를 책넘김 모드에서도
    // 열어주면 TOCPanel.tsx는 한 줄도 안 고쳐도 된다(검색 브릿지와 같은 패턴).
    window.pdfGoToPage = (page: number) => {
      flipbookViewRef.current?.jumpToPage(page, 'toc');
    };
    window.pdfSearchAllPages = async (query: string) => {
      const results: { pageNumber: number; text: string; excerpt: string }[] = [];
      if (!query.trim()) return results;

      const MAX_RESULTS = 50;
      const total = pdf.numPages;
      // S2-2 딥QA 수정(2026-09-14): query를 그대로 RegExp에 넣으면 정규식 특수문자를
      // 검색어로 입력한 사용자가 캐타스트로픽 백트래킹으로 자기 탭을 스스로 멈추게
      // 할 수 있었다 — 검색어는 항상 리터럴 문자열로 취급한다(PDFViewer.tsx와 동일).
      const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      let regex: RegExp;
      try {
        regex = new RegExp(escapeRegExp(query), 'gi');
      } catch {
        return results;
      }

      for (let p = 1; p <= total && results.length < MAX_RESULTS; p++) {
        let text = textCache.get(p);
        if (text === undefined) {
          try {
            const pg = await pdf.getPage(p);
            const textContent = await pg.getTextContent();
            text = textContent.items
              .map((item: any) => item.str)
              .join(' ')
              .replace(/\s+/g, ' ')
              .trim();
            textCache.set(p, text); // 성공했을 때만 캐시 — 실패는 다음 검색에서 재시도
          } catch (e) {
            console.error(`PDF 검색(책넘김): ${p}페이지 텍스트 추출 실패:`, e);
            // S2-2 딥QA 수정: 실패를 캐시하지 않는다 — PDFViewer.tsx와 동일한 이유.
            text = '';
          }
        }

        if (!text) continue;
        regex.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null && results.length < MAX_RESULTS) {
          const start = Math.max(0, match.index - 30);
          const end = Math.min(text.length, match.index + query.length + 30);
          results.push({
            pageNumber: p,
            text: match[0],
            excerpt: `...${text.substring(start, end)}...`,
          });
          if (match.index === regex.lastIndex) regex.lastIndex++;
        }
      }

      return results;
    };
  };

  // ✅ Extract pages for flipbook mode - PDF + EPUB 모두 지원
  useEffect(() => {
    if (viewMode !== 'flipbook' || !currentBook) {
      return;
    }

    const cached = flipbookCacheRef.current.get(currentBook.id);
    if (cached) {
      console.log(`📦 V2: 캐시된 플립북 페이지 재사용 — 재추출 생략 (${cached.pages.length}페이지)`);
      setFlipbookPages(cached.pages);
      setPageAspectRatio(cached.pageAspectRatio);
      // S2-2 딥QA 수정: 캐시 재사용 시에도 검색 브릿지를 다시 등록해야 한다 —
      // 스크롤 모드로 갔다 오면 PDFViewer.tsx의 언마운트 정리가
      // window.pdfSearchAllPages를 지워버리기 때문.
      if (cached.pdfDoc && cached.pdfTextCache) {
        registerFlipbookPdfBridge(cached.pdfDoc, cached.pdfTextCache);
      }
      if (cached.tocItems) {
        setTocItems(cached.tocItems, 'pdf');
      }
      if (cached.epubPageTextCache) {
        registerFlipbookEpubSearch(cached.epubPageTextCache);
        registerFlipbookEpubGoTo();
      }
      if (cached.epubTocItems) {
        setTocItems(cached.epubTocItems, 'epub');
      }
      return;
    }

    if (inFlightExtractionRef.current.has(currentBook.id)) {
      console.log(`⏳ 추출이 이미 진행 중 — 중복 실행 차단 (${currentBook.id})`);
      return;
    }

    const extractPages = async () => {
      setLoadingFlipbook(true);
      try {
        // ✅ PDF Flipbook 모드
        if (currentBook.fileType === 'pdf') {
          console.log('📖 PDF Flipbook 페이지 추출 시작...');
          const arrayBuffer = await getPDFFile(currentBook.filePath);
          if (!arrayBuffer) {
            console.error('PDF 파일을 찾을 수 없습니다');
            return;
          }

          // V3(현장보고 2026-08-26): "재추출 후 전 페이지 백지" — ArrayBuffer detach 가설
          // 검증용 로그. localforage(IndexedDB)는 getItem마다 구조적 복제로 새 버퍼를
          // 돌려주므로 이 값은 항상 원본 파일 크기와 같아야 한다. 0이면 detach가 실제
          // 원인이라는 뜻이니 이 줄부터 다시 볼 것.
          console.log(`🔬 PDF 재추출 진입 — arrayBuffer.byteLength=${arrayBuffer.byteLength}`);

          // pdf.js가 넘겨준 ArrayBuffer를 워커로 transfer(detach)한다는 것 자체는 사실이라
          // (MDN/pdf.js 문서에 명시) 값싼 방어로 얕은 복사본을 넘긴다. 단, 이 서버에서
          // 재현 시도(20p/7KB, 53p/782KB, 최대 5회 재추출)로는 detach가 한 번도 관찰되지
          // 않았다 — "확정된 원인"이 아니라 "공짜인 예방 조치"로 남겨두는 것. 실제 원인은
          // 재현 실패로 못 밝혔고, V2(재추출 자체 제거)가 구조적 해법이다 — 인계 노트 참고.
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise;
          const pages: FlipbookPage[] = [];
          const scale = 1.5; // 렌더링 품질
          // V1(현장보고 2026-08-26): FlipbookView의 pageRatio 1.4 하드코딩 제거용 —
          // 첫 페이지 뷰포트에서 실제 비율(height/width)을 한 번만 재서 내려보낸다.
          // 이 책은 194×260mm=1.34인데 1.4로 고정돼 있어 좌우 여백이 어긋났었다.
          let extractedAspectRatio: number | null = null;

          // SPRINT 1.11: 다음 장애 때 "탭이 숨겨져 있었나"를 로그 한 줄로 즉시 구분하기
          // 위한 것. 동작에는 영향 없다 — 아래 intent:'print'가 hidden이든 아니든 렌더를
          // 끝내주지만, 증상이 또 나오면 이 값부터 보라는 뜻이다.
          console.log(`[vis] ${document.visibilityState}`);

          for (let i = 1; i <= pdf.numPages; i++) {
            try {
              const page = await pdf.getPage(i);
              const viewport = page.getViewport({ scale });
              if (i === 1) {
                extractedAspectRatio = viewport.height / viewport.width;
              }

              // Canvas에 페이지 렌더링
              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d');
              canvas.width = viewport.width;
              canvas.height = viewport.height;

              if (context) {
                await page.render({
                  canvasContext: context,
                  viewport: viewport,
                  // ★SPRINT 1.11 장애 수정 — 원인: hidden 탭에서 rAF가 발화하지 않아
                  // render().promise가 영원히 pending된다(에러도 로그도 없이 정지).
                  // pdf.js의 InternalRenderTask는 렌더 청크 사이를 rAF로 이어가는데
                  // (pdfjs-dist 5.4.394 build/pdf.mjs:14861 `useRequestAnimationFrame:
                  // !intentPrint`), 탭이 백그라운드면 rAF가 안 와서 다음 청크로 못 넘어간다.
                  // 이 캔버스는 화면에 붙지 않는 오프스크린이라 rAF에 맞출 이유가 없다 —
                  // 'print' intent가 rAF 대신 즉시 continuation을 쓴다.
                  // PM 실브라우저 A/B 실측(hidden 탭, rAF 원본 그대로): 기본 intent는
                  // 12초 타임아웃, intent:'print'는 422ms 성공 + toDataURL 371,815자로
                  // 기본 intent가 성공했을 때와 결과 길이가 완전히 일치했다.
                  // ※전역 rAF 패치·setTimeout 폴링·visibilitychange 대기 같은 우회는
                  //   쓰지 말 것. 이 한 줄이 정답이다.
                  // ※화면에 실제로 보이는 뷰어(PDFViewer.tsx)에는 넣지 않는다 —
                  //   거기는 visible일 때만 그리는 게 맞다.
                  intent: 'print',
                } as any).promise;

                // Canvas를 이미지 URL로 변환
                const imageUrl = canvas.toDataURL('image/jpeg', 0.85);

                // R1-b(SPRINT 1.11 QA §3-1 정정): 이 페이지가 "이미지 단독 페이지"라는
                // 것을 CSS 선택자가 DOM 구조로 추측(:only-child)하게 두지 않고, 여기서
                // 생성하는 시점에 명시적으로 표시한다. :only-child는 img의 직계 부모
                // 안에 형제가 있는지만 보는데, EPUB 추출 파이프라인이 실제로 이미지를
                // <figure>로 감싸면(실측 확인) 페이지에 텍스트가 아무리 많아도 항상
                // "이미지 단독"으로 오판된다 — <a>·<div> 래핑에도 똑같이 무너진다.
                // "이미지 단독 페이지"를 만드는 곳은 이 PDF 추출부 하나뿐이므로, 여기서
                // pdf-image-page 클래스로 직접 표시하고 flipbook.css는 그 클래스만 본다.
                pages.push({
                  content: `<div class="pdf-image-page"><img src="${imageUrl}" alt="페이지 ${i}" style="width:100%;height:auto;" /></div>`,
                  pageNumber: i,
                });
              }

              // 메모리 해제
              canvas.width = 0;
              canvas.height = 0;

              // 진행 상황 로그 (10페이지마다)
              if (i % 10 === 0) {
                console.log(`   📄 ${i}/${pdf.numPages} 페이지 추출 완료`);
              }
            } catch (e) {
              console.warn(`페이지 ${i} 추출 실패:`, e);
            }
          }

          console.log(`✅ PDF Flipbook 준비 완료: ${pages.length} 페이지 (비율 ${extractedAspectRatio?.toFixed(3)})`);
          setFlipbookPages(pages);
          setPageAspectRatio(extractedAspectRatio);
          // S2-2 딥QA 수정: pdf.destroy()를 바로 부르면 검색이 쓸 문서 객체가
          // 사라진다. 페이지 이미지는 이미 캔버스로 다 뽑았으니(위 for문) 문서
          // 자체를 들고 있는 비용은 작다 — destroy는 Reader 언마운트 시로 미룬다.
          const pdfTextCache = new Map<number, string>();

          // 딥QA 수정(2026-09-14): TOC도 검색과 같은 구조적 벽이 있었다 — 책넘김
          // 모드에서는 PDFViewer.tsx가 마운트되지 않아 setTocItems가 한 번도 안
          // 불려서 목차 패널이 "불러오는 중..."에서 멈춰 있었다. 로직은 PDFViewer.tsx와
          // 동일(아웃라인 우선, 부족하면 페이지 기반 폴백).
          let flipbookTocItems: TocItem[] = [];
          try {
            const outline = await pdf.getOutline();
            if (outline && outline.length >= 5) {
              const convertOutline = async (items: any[], parentIndex: string = ''): Promise<TocItem[]> => {
                const result: TocItem[] = [];
                for (let oi = 0; oi < items.length; oi++) {
                  const item = items[oi];
                  let pageNumber = 1;
                  if (item.dest) {
                    try {
                      let dest = item.dest;
                      if (typeof dest === 'string') {
                        dest = await pdf.getDestination(dest);
                      }
                      if (dest && dest[0]) {
                        const pageIndex = await pdf.getPageIndex(dest[0]);
                        pageNumber = pageIndex + 1;
                      }
                    } catch (e) {
                      console.warn('페이지 번호 추출 실패:', e);
                    }
                  }
                  result.push({
                    id: `pdf-toc-${parentIndex}${oi}`,
                    label: item.title || `항목 ${oi + 1}`,
                    href: '',
                    pageNumber,
                    subitems: item.items?.length ? await convertOutline(item.items, `${parentIndex}${oi}-`) : undefined,
                  });
                }
                return result;
              };
              flipbookTocItems = await convertOutline(outline);
            } else {
              const step = pdf.numPages > 100 ? 10 : (pdf.numPages > 50 ? 5 : 1);
              for (let pi = 1; pi <= pdf.numPages; pi += step) {
                flipbookTocItems.push({ id: `pdf-page-${pi}`, label: `${pi}페이지`, href: '', pageNumber: pi });
              }
              if (flipbookTocItems.length > 0 && flipbookTocItems[flipbookTocItems.length - 1].pageNumber !== pdf.numPages) {
                flipbookTocItems.push({ id: `pdf-page-${pdf.numPages}`, label: `${pdf.numPages}페이지 (마지막)`, href: '', pageNumber: pdf.numPages });
              }
            }
            setTocItems(flipbookTocItems, 'pdf');
          } catch (outlineErr) {
            console.warn('PDF 아웃라인 로드 실패(책넘김):', outlineErr);
          }

          if (extractionCancelledRef.current.has(currentBook.id)) {
            // 이미 화면을 떠났다 — 캐시에 넣지도, 전역 창구를 등록하지도 않고 문서를 닫는다.
            extractionCancelledRef.current.delete(currentBook.id);
            pdf.destroy();
            return;
          }
          flipbookCacheRef.current.set(currentBook.id, { pages, pageAspectRatio: extractedAspectRatio, pdfDoc: pdf, pdfTextCache, tocItems: flipbookTocItems }); // V2: 다음 토글부터 재추출 생략
          registerFlipbookPdfBridge(pdf, pdfTextCache);

        } else {
          // ✅ EPUB Flipbook 모드 — 챕터를 적절한 크기로 분할
          console.log('📖 EPUB Flipbook 페이지 추출 시작...');
          const blob = await getEPUBFile(currentBook.filePath);
          if (!blob) {
            console.error('EPUB 파일을 찾을 수 없습니다');
            return;
          }

          const arrayBuffer = await blob.arrayBuffer();
          const book = ePub(arrayBuffer);
          // SPRINT 2.2-a: book.ready는 manifest/spine/resources 등이 채워지면
          // 바로 resolve된다 — epub.js가 archive 로드 시 내부적으로 자동
          // 트리거하는 resources.replacements()/replaceCss() 체인(우리는 안 쓰는
          // 기능, EPUB을 blob URL로 열 때 항상 자동 실행됨)은 기다려주지 않는다.
          // 이 함수 끝에서 book.destroy()를 부르는데, 그 체인이 아직 안 끝난
          // 상태에서 destroy()가 this.resources를 지워버리면 나중에 체인이
          // resolve될 때 "Cannot read properties of undefined (reading
          // 'replaceCss')"가 뜬다(실측: 콘솔에 매번 재현, node_modules/epubjs
          // 소스로 확인 — Book.opening은 그 체인까지 기다린 뒤에만 resolve됨).
          // book.opened(=book.opening.promise)를 같이 기다려 그 체인이 우리가
          // book을 파괴하기 전에 반드시 끝나게 한다.
          await Promise.all([book.ready, book.opened]);

          // EPUB CSS 로드 (원본 스타일 유지)
          let epubCSS = '';
          try {
            const manifest = (book.packaging as any)?.manifest;
            if (manifest) {
              for (const [, item] of Object.entries(manifest) as any) {
                if (item.type === 'text/css' || item.href?.endsWith('.css')) {
                  try {
                    const cssText = await (book.archive as any).getText(item.href);
                    if (cssText) epubCSS += cssText + '\n';
                  } catch { /* skip */ }
                }
              }
            }
          } catch { /* skip */ }

          const spine = book.spine as any;
          const pages: FlipbookPage[] = [];
          let pageNum = 1;
          const imageCache = new Map<string, string>();

          // 이미지 경로를 blob URL로 변환하는 헬퍼
          const resolveImages = async (html: string, itemHref: string): Promise<string> => {
            const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
            const matches = [...html.matchAll(imgRegex)];
            for (const match of matches) {
              const src = match[1];
              if (src.startsWith('blob:') || src.startsWith('data:') || src.startsWith('http')) continue;
              try {
                if (imageCache.has(src)) {
                  html = html.split(src).join(imageCache.get(src)!);
                  continue;
                }
                const basePath = itemHref.substring(0, itemHref.lastIndexOf('/') + 1);
                // SPRINT 2.2-a: 두 겹 버그였다(§7-2에 등재된 것과 같은 계열,
                // 여기서 같이 닫음).
                // ① basePath + src로 그냥 이어붙이면 src가 "../"로 시작할 때
                //    "Text/../Images/cover.png" 같은 정규화 안 된 경로가 됨
                //    → resolveEpubPath로 "../"를 실제로 접어야 한다.
                // ② book.archive.getBlob()은 OPF 파일(content.opf) 기준 상대
                //    경로가 아니라 **zip 루트 기준 절대경로**("/OEBPS/Images/...")
                //    를 기대한다(zip 안 실제 엔트리가 "OEBPS/Images/cover.png"임을
                //    JSZip로 직접 확인) — book.load(item.href)가 항상 되던 건
                //    그 내부에서 book.resolve()를 거쳐 OEBPS/ 접두사를 붙이기
                //    때문이었다. archive.getBlob()을 직접 부를 때도 같은
                //    book.resolve()를 거쳐야 한다 — 실측으로 확인(book.resolve
                //    없이 "Images/cover.png"만 넘기면 항상 실패, book.resolve로
                //    "/OEBPS/Images/cover.png"를 만들어 넘기면 항상 성공).
                const resolvedPath = src.startsWith('/') ? src.slice(1) : resolveEpubPath(basePath, src);
                const imgBlob = await (book.archive as any).getBlob(book.resolve(resolvedPath));
                if (imgBlob) {
                  const blobUrl = URL.createObjectURL(imgBlob);
                  imageCache.set(src, blobUrl);
                  html = html.split(src).join(blobUrl);
                }
              } catch { /* skip */ }
            }
            return html;
          };

          // ✅ 블록 요소 단위로 분할 (p, h1~h6, div, section, table, ul, ol, blockquote, pre, figure, hr)
          const CHARS_PER_PAGE = 1200; // 플립북 한 페이지에 표시할 대략적 글자 수

          for (const item of spine.items) {
            try {
              const doc = await book.load(item.href);
              if (!doc) continue;

              let fullHTML = (doc as Document).body?.innerHTML || '';
              if (!fullHTML.trim()) continue;

              fullHTML = await resolveImages(fullHTML, item.href);

              // CSS 스타일 래퍼 (원본 EPUB 스타일 적용)
              const styleWrapper = epubCSS
                ? `<style>${epubCSS}</style>`
                : '';

              // 텍스트 길이로 분할 필요 여부 판단
              const textLength = fullHTML.replace(/<[^>]+>/g, '').length;

              if (textLength <= CHARS_PER_PAGE) {
                // 짧은 챕터 — 그대로 1페이지
                pages.push({
                  content: styleWrapper + fullHTML,
                  pageNumber: pageNum++,
                });
              } else {
                // 긴 챕터 — 블록 요소 단위로 분할
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = fullHTML;

                const blocks = Array.from(tempDiv.children);
                let currentChunk = '';
                let currentLen = 0;

                for (const block of blocks) {
                  const blockHTML = (block as HTMLElement).outerHTML;
                  const blockTextLen = (block as HTMLElement).textContent?.length || 0;

                  if (currentLen > 0 && currentLen + blockTextLen > CHARS_PER_PAGE) {
                    // 현재 청크를 페이지로 저장
                    pages.push({
                      content: styleWrapper + currentChunk,
                      pageNumber: pageNum++,
                    });
                    currentChunk = blockHTML;
                    currentLen = blockTextLen;
                  } else {
                    currentChunk += blockHTML;
                    currentLen += blockTextLen;
                  }
                }

                // 마지막 남은 청크
                if (currentChunk.trim()) {
                  pages.push({
                    content: styleWrapper + currentChunk,
                    pageNumber: pageNum++,
                  });
                }
              }
            } catch (e) {
              console.warn('페이지 추출 실패:', item.href);
            }
          }

          console.log(`✅ EPUB Flipbook 준비 완료: ${pages.length} 페이지`);
          setFlipbookPages(pages);
          setPageAspectRatio(null); // EPUB은 고정 페이지 이미지가 없어 실측 비율이 없음 — FlipbookView 기본값 사용

          // PM 결정(2026-09-14): 검색용 페이지별 평문을 여기서 한 번에 뽑아 둔다.
          // pages[i].content는 이미 flipbook 페이지 번호와 1:1이라, 태그만 벗기면
          // "검색 결과 → 그 페이지로 이동"이 바로 성립한다(별도 매핑 불필요).
          // book.destroy()는 그대로 둔다 — 평문을 떠 놨으니 epub.js 객체를 살려둘 이유가 없다.
          const epubPageTextCache = new Map<number, string>();
          const tagStripper = document.createElement('div');
          for (const p of pages) {
            tagStripper.innerHTML = p.content;
            const plain = (tagStripper.textContent || '').replace(/\s+/g, ' ').trim();
            if (plain) epubPageTextCache.set(p.pageNumber, plain);
          }
          tagStripper.innerHTML = '';

          // PM 결정(2026-09-14): 목차도 여기서 떠 둔다. book.destroy() 전에 nav를 읽어야
          // 하므로 추출이 끝난 이 시점이 유일한 기회다. 변환 규칙은 EPUBViewer.tsx와 동일.
          let epubTocItems: TocItem[] = [];
          try {
            const nav: any = await book.loaded.navigation;

            // ★ nav의 href는 nav 문서 기준 상대경로라 spine 경로와 다를 수 있다
            //   (실측: nav는 "chapter12.xhtml", spine은 "Text/chapter12.xhtml").
            //   그대로 rendition.display()에 넘기면 resolve가 안 돼 promise가 영영
            //   안 끝나고 리더가 "불러오는 중"에서 멈춘다. 파일명으로 spine 항목을
            //   찾아 spine 기준 href로 바꿔 둔다.
            const spineHrefs: string[] = ((book.spine as any)?.items || []).map((s: any) => s.href);
            const toSpineHref = (href: string): string => {
              if (!href) return '';
              const [rawPath, hash] = href.split('#');
              const base = rawPath.split('/').pop() || rawPath;
              const match = spineHrefs.find((s) => (s.split('/').pop() || s) === base);
              const resolved = match || rawPath;
              return hash ? `${resolved}#${hash}` : resolved;
            };

            const convertToc = (items: any[]): TocItem[] =>
              items.map((item, index) => ({
                id: item.id || `toc-${index}`,
                label: item.label || '제목 없음',
                href: toSpineHref(item.href || ''),
                subitems: item.subitems?.length ? convertToc(item.subitems) : undefined,
              }));
            if (nav?.toc?.length) {
              epubTocItems = convertToc(nav.toc);
              setTocItems(epubTocItems, 'epub');
            }
          } catch (navErr) {
            console.warn('EPUB 목차 로드 실패(책넘김):', navErr);
          }

          if (extractionCancelledRef.current.has(currentBook.id)) {
            extractionCancelledRef.current.delete(currentBook.id);
            imageCache.forEach((url) => URL.revokeObjectURL(url)); // 안 쓸 blob은 여기서 바로 반납
            book.destroy();
            return;
          }
          flipbookCacheRef.current.set(currentBook.id, { pages, pageAspectRatio: null, epubPageTextCache, epubTocItems, epubImageUrls: Array.from(imageCache.values()) }); // V2: 다음 토글부터 재추출 생략
          registerFlipbookEpubSearch(epubPageTextCache);
          registerFlipbookEpubGoTo();
          book.destroy();
        }
      } catch (err) {
        console.error('Flipbook 페이지 추출 실패:', err);
      } finally {
        setLoadingFlipbook(false);
      }
    };

    const bookId = currentBook.id;
    const promise = extractPages().finally(() => {
      inFlightExtractionRef.current.delete(bookId);
    });
    inFlightExtractionRef.current.set(bookId, promise);

    // 딥QA 재점검(2026-09-14): 추출은 비동기라 도중에 화면을 떠날 수 있다. 그때
    // 언마운트 정리가 먼저 돌고 추출이 나중에 끝나면, 이미 비워진 캐시에 pdfDoc과
    // blob URL을 새로 넣고(아무도 해제 못 함) 죽은 Reader에 전역 창구까지 다시
    // 등록해버린다 — 다음 책에서 이전 책 검색 결과가 나오는 원인이 된다.
    // 취소 신호를 내려 추출 쪽이 마무리 직전에 스스로 접도록 한다.
    return () => {
      extractionCancelledRef.current.add(bookId);
    };
  }, [viewMode, currentBook]);

  // S2-3(2026-09-14): 책넘김 모드 TTS. 종전엔 PDFViewer/EPUBViewer가 언마운트돼
  // pdfGetCurrentText/epubGetCurrentText가 없으니 헤더가 TTS 버튼을 아예 비활성화해
  // 뒀다(V5 임시 조치). 검색용으로 이미 만들어 둔 페이지별 텍스트 캐시를 그대로
  // 재사용하면 "지금 펼쳐진 쪽"의 텍스트를 그 자리에서 돌려줄 수 있다.
  // 스프레드(두 쪽 펼침)면 보이는 두 쪽을 순서대로 이어 읽는다 — spreadMap의
  // visiblePages가 그 판정의 정본이다(S2-1에서 이미 검증된 함수).
  useEffect(() => {
    window.flipbookGetCurrentText = async () => {
      if (viewMode !== 'flipbook' || !currentBook) return '';
      const cached = flipbookCacheRef.current.get(currentBook.id);
      if (!cached) return '';

      const total = cached.pages.length;
      const wanted = visiblePages(flipbookCurrentPage, total, flipbookIsPortrait);
      const chunks: string[] = [];

      for (const pageNumber of wanted) {
        if (cached.epubPageTextCache) {
          const t = cached.epubPageTextCache.get(pageNumber);
          if (t) chunks.push(t);
          continue;
        }
        // PDF: 검색 캐시에 없으면 그 페이지만 즉석에서 뽑아 캐시에 넣는다.
        const textCache = cached.pdfTextCache;
        const pdf = cached.pdfDoc;
        if (!textCache || !pdf) continue;
        let t = textCache.get(pageNumber);
        if (t === undefined) {
          try {
            const pg = await pdf.getPage(pageNumber);
            const content = await pg.getTextContent();
            const extracted = content.items
              .map((item: any) => item.str)
              .join(' ')
              .replace(/\s+/g, ' ')
              .trim();
            textCache.set(pageNumber, extracted);
            t = extracted;
          } catch (e) {
            console.error(`TTS: ${pageNumber}페이지 텍스트 추출 실패:`, e);
            t = '';
          }
        }
        if (t) chunks.push(t);
      }

      return chunks.join(' ').trim();
    };

    return () => {
      window.flipbookGetCurrentText = undefined;
    };
  }, [viewMode, currentBook, flipbookCurrentPage, flipbookIsPortrait]);

  // S2-2 딥QA 수정: Reader 자체가 사라질 때(라이브러리로 나갈 때) 책넘김 모드용
  // pdf.js 문서들을 정리한다 — flipbookCacheRef에 쌓인 pdfDoc들은 pdf.destroy()를
  // 미뤄온 것들이라 여기서 한 번에 해제하지 않으면 메모리에 계속 남는다.
  useEffect(() => {
    return () => {
      window.pdfSearchAllPages = undefined;
      window.pdfGoToPage = undefined;
      window.epubSearchAllPages = undefined;
      window.epubGoTo = undefined;
      if (pendingGoToTimerRef.current !== null) {
        clearInterval(pendingGoToTimerRef.current);
        pendingGoToTimerRef.current = null;
      }
      flipbookCacheRef.current.forEach((entry) => {
        entry.pdfDoc?.destroy();
        entry.epubImageUrls?.forEach((url) => URL.revokeObjectURL(url));
      });
      clearToc();
    };
  }, []);

  if (!currentBook) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-600 dark:text-gray-400">책을 선택해주세요</p>
      </div>
    );
  }

  return (
    <div className="reader h-screen flex flex-col overflow-hidden">
      {/* Header - ✅ 보기 모드 토글 통합 (1줄) */}
      <ReaderHeader
        book={currentBook}
        currentLocation={currentLocation}
        onToggleHighlights={() => setShowHighlights(!showHighlights)}
        showHighlights={showHighlights}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onToggleSearch={() => setShowSearch(!showSearch)}
        onToggleBookmarks={() => setShowBookmarks(!showBookmarks)}
        showBookmarks={showBookmarks}
        onToggleThumbnails={() => setShowThumbnails(!showThumbnails)}
        showThumbnails={showThumbnails}
        onToggleAudio={() => setShowAudio(!showAudio)}
        showAudio={showAudio}
        showSearch={showSearch}
        onToggleShare={() => setShowShare(true)}
      />

      {/* Main Reader Area - ✅ P1-14: TOC 열림 시 책 영역 push */}
      <div className="flex-1 relative min-h-0 flex flex-row">
        {/* P1-14: TOC Panel — flex 왼쪽에 배치, 책 영역 push */}
        {showTOC && (
          <div className="h-full flex-shrink-0 z-10 border-r border-gray-200 dark:border-gray-700">
            <TOCPanel />
          </div>
        )}

        {/* 메인 콘텐츠 영역 (flex-1: TOC 열리면 자동 축소) */}
        <div className="flex-1 relative min-w-0 h-full">
          {/* PDF/EPUB/Flipbook 자동 선택 - ✅ PDF + EPUB 모두 Flipbook 지원 */}
          {viewMode === 'flipbook' ? (
            // Flipbook Mode
            loadingFlipbook ? (
              <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                  <div className="animate-spin w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">페이지 추출 중...</p>
                </div>
              </div>
            ) : flipbookPages.length > 0 ? (
              <FlipbookView
                ref={flipbookViewRef}
                pages={flipbookPages}
                currentPage={flipbookCurrentPage}
                onPageChange={setFlipbookCurrentPage}
                fontSize={readerSettings.fontSize}
                fontFamily={readerSettings.fontFamily}
                theme={readerSettings.theme}
                soundEnabled={true}
                bookId={currentBook.id}
                onSearchOpen={() => setShowSearch(true)}
                pageAspectRatio={pageAspectRatio}
                bottomInsetPx={thumbnailStripHeight}
                onFooterHeightChange={setFlipbookFooterHeight}
                onPortraitChange={setFlipbookIsPortrait}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500 dark:text-gray-400">페이지를 불러올 수 없습니다</p>
              </div>
            )
          ) : currentBook.fileType === 'pdf' ? (
            // ✅ PDF Scroll Mode
            <PDFViewer book={currentBook} />
          ) : (
            // ✅ EPUB Scroll Mode
            <EPUBViewer
              book={currentBook}
              onLocationChange={setCurrentLocation}
            />
          )}

          {/* Settings Panel (Overlay) */}
          {showSettings && (
            <div className="absolute top-0 right-0 h-full z-10">
              <SettingsPanel />
            </div>
          )}

          {/* Highlights Panel (Overlay) */}
          {showHighlights && (
            <div className="absolute top-0 right-0 h-full z-10">
              <HighlightsPanel
                onJumpToHighlight={(highlight) => {
                  // PM 결정(2026-09-14): 종전엔 onJumpToHighlight가 아예 연결돼 있지 않아
                  // 하이라이트를 눌러도 어떤 모드에서도 아무 일이 없었다(미구현 상태).
                  // PDF 하이라이트는 CFI가 없고 쪽 번호를 들고 있으므로 갈래를 나눈다.
                  if (typeof highlight.pageNumber === 'number') {
                    if (viewMode === 'flipbook') {
                      flipbookViewRef.current?.jumpToPage(highlight.pageNumber, 'bookmark');
                    } else {
                      window.pdfGoToPage?.(highlight.pageNumber);
                    }
                  } else {
                    openEpubLocation(highlight.cfiRange);
                  }
                  setShowHighlights(false);
                }}
                onJumpToNote={(note) => {
                  // 하이라이트와 같은 갈래 — PDF 메모는 쪽 번호를 들고 있다.
                  if (typeof note.pageNumber === 'number') {
                    if (viewMode === 'flipbook') {
                      flipbookViewRef.current?.jumpToPage(note.pageNumber, 'bookmark');
                    } else {
                      window.pdfGoToPage?.(note.pageNumber);
                    }
                  } else {
                    openEpubLocation(note.cfiRange);
                  }
                  setShowHighlights(false);
                }}
              />
            </div>
          )}

          {/* ✅ Search Panel (Overlay) */}
          <SearchPanel
            // PM 결정(2026-09-14): 모드를 바꾸면 이전 모드에서 만든 검색 결과는 못 쓴다
            // (스크롤 결과는 CFI, 책넘김 결과는 페이지 번호라 서로 이동 수단이 다르다).
            // key로 패널을 새로 마운트해 결과를 비운다 — 안 그러면 옛 결과가 남아 있고,
            // 그 상태에서 Enter는 "재검색"이 아니라 "현재 결과로 이동"이라 클릭해도
            // 아무 일이 없는 것처럼 보인다(실측 확인).
            key={viewMode}
            isOpen={showSearch}
            onClose={() => setShowSearch(false)}
            fileType={currentBook.fileType as 'epub' | 'pdf'}
            onNavigate={(result) => {
              // B5: 플립북 모드 + 페이지 번호가 있는 결과(현재는 PDF)만 실제 이동 가능.
              // EPUB은 CFI만 갖고 있어 플립북 페이지 번호로 매핑할 방법이 없음 — 범위 밖, 인계 노트 참고.
              if (viewMode === 'flipbook' && typeof result.pageNumber === 'number') {
                flipbookViewRef.current?.jumpToPage(result.pageNumber);
              } else if (typeof result.pageNumber === 'number' && window.pdfGoToPage) {
                // 딥QA 재점검(2026-09-14): 스크롤 모드의 PDF 결과는 cfi가 없어 아래
                // openEpubLocation 갈래로 새다가 console.log만 찍고 끝났다 — 하이라이트는
                // 이미 쪽 번호로 이동하게 고쳐뒀는데 검색 결과만 빠져 있었다.
                window.pdfGoToPage(result.pageNumber);
              } else if (openEpubLocation(result.cfi)) {
                // PM 결정(2026-09-14): EPUB — 전체 검색이 돌려준 spine href로 이동.
                // 종전엔 여기가 console.log뿐이라(37e56de "범위 밖") EPUB은 검색 결과를
                // 눌러도 아무 일도 일어나지 않았다. 책넘김 모드라 CFI로 갈 수 없으면
                // openEpubLocation이 스크롤 모드로 전환한 뒤 그 위치를 연다.
              } else {
                console.log('검색 결과 이동(미지원 케이스):', result);
              }
            }}
          />
        </div>
      </div>

      {/* Controls - EPUBViewer 내부에 포함됨 (중복 제거) */}

      {/* ✅ Share Modal */}
      {showShare && (
        <ShareModal
          onClose={() => setShowShare(false)}
          bookTitle={currentBook.title}
          bookAuthor={currentBook.author}
        />
      )}

      {/* ✅ Bookmarks Panel */}
      {showBookmarks && (
        <BookmarksPanel
          isOpen={showBookmarks}
          onClose={() => setShowBookmarks(false)}
          bookId={currentBook.id}
          fileType={currentBook.fileType as 'epub' | 'pdf'}
          currentPage={flipbookCurrentPage}
          currentProgress={flipbookPages.length > 0 ? (flipbookCurrentPage / flipbookPages.length) * 100 : 0}
          onNavigate={(bookmark) => {
            console.log('북마크 이동:', bookmark);
            // 딥QA 수정(2026-09-14): 책넘김 모드에서 setFlipbookCurrentPage만 부르면
            // FlipbookView가 currentPage prop 변화를 감지해 화면은 넘어가지만(위 null-check
            // 수정으로 그 경로 자체는 안전해짐), jumpToPage가 하는 뒤로가기 스택 기록
            // (prevPageStack/isJumpNavRef, history pushState)은 건너뛴다 — 북마크로 갔다가
            // "뒤로가기"를 눌러도 원래 보던 페이지로 못 돌아온다. 검색 결과 이동과 같은
            // 경로(jumpToPage)로 통일한다.
            if (viewMode === 'flipbook' && typeof bookmark.pageNumber === 'number') {
              flipbookViewRef.current?.jumpToPage(bookmark.pageNumber, 'bookmark');
            } else if (bookmark.pageNumber) {
              setFlipbookCurrentPage(bookmark.pageNumber);
            }
            setShowBookmarks(false);
          }}
        />
      )}

      {/* ✅ Thumbnail Navigation */}
      <ThumbnailNav
        isOpen={showThumbnails}
        onToggle={() => setShowThumbnails(!showThumbnails)}
        totalPages={flipbookPages.length || 1}
        currentPage={flipbookCurrentPage}
        thumbnails={flipbookPages.map((p, i) => ({
          pageNumber: i + 1,
          thumbnail: p.content.includes('<img') ? p.content.match(/src="([^"]+)"/)?.[1] : undefined,
        }))}
        onPageSelect={(page) => setFlipbookCurrentPage(page)}
        fileType={currentBook.fileType as 'epub' | 'pdf'}
        onHeightChange={setThumbnailStripHeight}
        footerClearancePx={flipbookFooterHeight}
        isPortrait={flipbookIsPortrait}
      />

      {/* ✅ Background Audio */}
      <BackgroundAudio
        isOpen={showAudio}
        onClose={() => setShowAudio(false)}
      />
    </div>
  );
};

export default Reader;
